<template>
  <CCard class="settings-card">
    <CCardHeader>
      <div class="section-heading">
        <span class="section-icon"><CIcon name="cil-lock-locked"/></span>
        <div>
          <strong>2要素認証</strong>
          <p>
            パスワードに加えて、認証アプリの6桁を使います。
            パスワードが漏れても、それだけでは入れなくなります。
          </p>
        </div>
      </div>
    </CCardHeader>

    <CCardBody>
      <div v-if="loading" class="security-settings-loading">
        <CSpinner color="primary"/>
        <span>状態を読み込んでいます。</span>
      </div>

      <template v-else>
        <CAlert
          v-if="errorMessage"
          color="danger"
          close-button
          @update:show="errorMessage = ''"
        >{{ errorMessage }}</CAlert>

        <CAlert
          v-if="message"
          color="success"
          close-button
          @update:show="message = ''"
        >{{ message }}</CAlert>

        <!--
          予備のコードは、出したその場でしか見せられない。
          **ほかの表示より先に置く。** 下に置くと、画面が短い端末で
          気づかれないまま閉じられる。
        -->
        <div v-if="backupCodes.length" class="mfa-backup">
          <p class="mfa-backup__title">
            予備のコード（<b>いま控えてください</b>）
          </p>
          <p class="mfa-backup__note">
            スマホを失くしたときに、これでログインします。
            <b>この画面を閉じると二度と見られません。</b>
            1本につき1回だけ使えます。
          </p>
          <ul class="mfa-backup__list">
            <li v-for="code in backupCodes" :key="code">{{ code }}</li>
          </ul>
          <CButton color="primary" @click="acknowledgeCodes">
            控えました
          </CButton>
        </div>

        <!-- 登録の途中 -->
        <div v-else-if="enrolling" class="mfa-enroll">
          <p class="mfa-enroll__step">
            <b>1.</b> 認証アプリ（Google Authenticator、Microsoft
            Authenticator など）で、このQRコードを読み取ります。
          </p>

          <img
            v-if="qrImage"
            class="mfa-enroll__qr"
            :src="qrImage"
            alt="認証アプリで読み取るQRコード"
          >

          <!--
            QRが読めないときの逃げ道。**畳んでおく。**

            開いたまま鍵を大きく出すと、その下の「6桁」を聞かれた人が
            **鍵のほうを入れるものだと読む。**実際にそう読まれた。
          -->
          <details class="mfa-enroll__manual">
            <summary>QRコードを読み取れないとき</summary>
            <p class="mfa-enroll__note">
              認証アプリの「手動で入力」から、次の鍵を登録してください。
              アカウント名は、ご自分のメールアドレスにします。
            </p>
            <p class="mfa-enroll__secret">{{ secret }}</p>
          </details>

          <p class="mfa-enroll__step">
            <b>2.</b> 登録できたら、<b>アプリの画面に出ている6桁の数字</b>を
            入れてください。（上の鍵ではありません）
          </p>
          <CInput
            v-model="code"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="8"
            placeholder="6桁"
            :disabled="busy"
          />

          <div class="mfa-enroll__actions">
            <CButton
              color="primary"
              :disabled="busy || !code"
              @click="confirm"
            >登録する</CButton>
            <CButton
              color="link"
              :disabled="busy"
              @click="cancelEnroll"
            >やめる</CButton>
          </div>
        </div>

        <!--
          認証アプリの付け替え（場面 X-08）。

          2026-08-24 まで、機種変更した人は**どちらの道からも出られな
          かった**。やめる側は会社の方針に断られ、登録し直す側は
          「もう確かめ済みです」で断られる。頼れるのは別の管理者だけで、
          オーナーが1人の会社では誰にも頼めなかった。
        -->
        <div v-else-if="rotating === 'verify'" class="mfa-enroll">
          <p class="mfa-enroll__step">
            <b>1.</b> <b>いま使っている</b>認証アプリの6桁を入れてください。
          </p>
          <p class="mfa-enroll__note">
            古い端末が手元に無いときは、予備のコードでもかまいません。
            <b>ここではまだ何も変わりません。</b>
          </p>
          <CInput
            v-model="code"
            type="text"
            inputmode="text"
            autocomplete="one-time-code"
            maxlength="16"
            placeholder="6桁 または 予備のコード"
            :disabled="busy"
          />
          <div class="mfa-enroll__actions">
            <CButton
              color="primary"
              :disabled="busy || !code"
              @click="beginRotation"
            >次へ</CButton>
            <CButton
              color="link"
              :disabled="busy"
              @click="cancelRotation"
            >やめる</CButton>
          </div>
        </div>

        <div v-else-if="rotating === 'scan'" class="mfa-enroll">
          <p class="mfa-enroll__step">
            <b>2.</b> <b>新しい端末</b>の認証アプリで、このQRコードを
            読み取ります。
          </p>

          <img
            v-if="qrImage"
            class="mfa-enroll__qr"
            :src="qrImage"
            alt="新しい端末で読み取るQRコード"
          >

          <details class="mfa-enroll__manual">
            <summary>QRコードを読み取れないとき</summary>
            <p class="mfa-enroll__note">
              認証アプリの「手動で入力」から、次の鍵を登録してください。
            </p>
            <p class="mfa-enroll__secret">{{ secret }}</p>
          </details>

          <p class="mfa-enroll__note">
            <b>まだ古い端末が使えます。</b>ここでやめても、いまの
            認証アプリはそのまま使えます。
          </p>

          <p class="mfa-enroll__step">
            <b>3.</b> <b>新しい端末に出ている6桁の数字</b>を
            入れてください。
          </p>
          <CInput
            v-model="code"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="8"
            placeholder="6桁"
            :disabled="busy"
          />
          <div class="mfa-enroll__actions">
            <CButton
              color="primary"
              :disabled="busy || !code"
              @click="finishRotation"
            >付け替える</CButton>
            <CButton
              color="link"
              :disabled="busy"
              @click="cancelRotation"
            >やめる</CButton>
          </div>
          <p class="security-settings-note">
            付け替えると、いま持っている予備のコードは全部使えなくなります。
            新しいぶんをその場でお渡しします。
          </p>
        </div>

        <!-- 使っている -->
        <template v-else-if="status.enabled">
          <p class="mfa-state mfa-state--on">
            <CIcon name="cil-check-circle"/>
            2要素認証を使っています。
          </p>
          <p class="mfa-remaining">
            残りの予備のコード: <b>{{ status.backupCodesRemaining }}</b> 本
            <span v-if="status.backupCodesRemaining === 0" class="mfa-remaining__warn">
              — 0本です。スマホを失くすと入れなくなります。配り直してください。
            </span>
          </p>
          <div class="mfa-actions">
            <CButton
              color="outline-primary"
              :disabled="busy"
              @click="reissue"
            >予備のコードを配り直す</CButton>
            <CButton
              color="outline-primary"
              :disabled="busy"
              @click="askCurrentFactor"
            >認証アプリを新しい端末へ付け替える</CButton>
            <CButton
              color="outline-danger"
              :disabled="busy"
              @click="disable"
            >2要素認証をやめる</CButton>
          </div>
          <p class="security-settings-note">
            配り直すと、いま持っている予備のコードは全部使えなくなります。
          </p>
          <p class="security-settings-note">
            スマホを買い替えたときは「付け替える」を使ってください。
            <b>やめる必要はありません。</b>
          </p>
        </template>

        <!-- まだ使っていない -->
        <template v-else>
          <p class="mfa-state">まだ使っていません。</p>
          <CButton
            color="primary"
            :disabled="busy"
            @click="beginEnroll"
          >2要素認証を設定する</CButton>
        </template>
      </template>
    </CCardBody>
  </CCard>
</template>

<script>
import QRCode from 'qrcode'

import {
  getMfaStatus,
  startEnrollment,
  confirmEnrollment,
  reissueBackupCodes,
  startRotation,
  confirmRotation,
  cancelRotation,
  disableMfa,
  isReauthenticationRequired,
  isForbiddenByPolicy,
  serverMessage,
  isNetworkFailure
} from '../../services/mfa'

/**
 * 自分で2要素認証を付ける・外す画面。
 *
 * <h3>強制される流れとは別</h3>
 *
 * <p>会社が管理者に必須にしたときの登録は、ログイン画面（Login.vue）で
 * 通る。あちらは<b>まだログインしていない人</b>が通る道で、こちらは
 * <b>もうログインしている人</b>が自分から付ける道。サーバの口も別。</p>
 *
 * <h3>予備のコードは1回しか見せられない</h3>
 *
 * <p>出したその場で控えてもらう。控えたかどうかをサーバは確かめられない
 * ので、<b>画面がいちばん上に出し、閉じる前に押させる</b>。</p>
 *
 * <h3>15分を過ぎていると断られる</h3>
 *
 * <p>登録・解除・配り直しは、ログインから15分以内でないとサーバが断る。
 * 席を外した隙や盗まれたCookieで2要素を差し替えられないようにするため。
 * <b>いまは操作の前だけ確認し直す口が無い</b>ので、ログインし直してもらう。</p>
 */
export default {
  name: 'MfaSettings',

  data () {
    return {
      status: { enabled: false, backupCodesRemaining: 0 },
      loading: true,
      busy: false,
      enrolling: false,
      /* '' / 'verify'（いまの要素を出す） / 'scan'（新しい端末を登録） */
      rotating: '',
      /* この1回ぶんの名前。**2つのタブを混ぜないために要る。** */
      rotationId: '',
      secret: '',
      /**
       * 認証アプリへ読ませるQRコード（data URL）。
       *
       * **ブラウザの中だけで作る。**外のQR画像サービスへ渡すと、
       * 2要素の共有鍵がその会社へ丸ごと渡る。鍵が渡れば、6桁は
       * いつでも作れる。
       */
      qrImage: '',
      code: '',
      backupCodes: [],
      message: '',
      errorMessage: ''
    }
  },

  mounted () {
    this.reload()
  },

  methods: {
    async reload () {
      this.loading = true
      try {
        this.status = await getMfaStatus()
      } catch (error) {
        this.errorMessage = this.explain(error,
          '2要素認証の状態を読み込めませんでした。')
      } finally {
        this.loading = false
      }
    },

    /**
     * サーバが返した otpauth:// から、QRを作る。
     *
     * <h3>外へ出さない</h3>
     *
     * <p><b>QR画像を作るサービスへ渡してはいけない。</b>otpauth:// には
     * 2要素の共有鍵がそのまま入っている。渡した先は、その人の6桁を
     * いつでも作れる。ここでは端末の中だけで描く。</p>
     *
     * <h3>作れなくても止めない</h3>
     *
     * <p>QRが出なくても、鍵を手で入力すれば登録できる。ここで例外を
     * 投げると<b>登録そのものが始められなくなる。</b></p>
     */
    async drawQr (options) {
      const uri = (options && options.otpauthUri) || ''
      if (!uri) {
        this.qrImage = ''
        return
      }
      try {
        /*
         * **SVGで描く。**canvas を使う道もあるが、
         *
         *   - 拡大しても粗くならない（印刷して配る会社がある）
         *   - canvas が無い場所でも動く（検査は jsdom で走る。
         *     canvas が無いと toDataURL が落ち、**QRが出ないことに
         *     気づけないまま緑になる**）
         *
         * 出来上がりは data: の画像なので、外へは何も出ない。
         */
        const svg = await QRCode.toString(uri, {
          type: 'svg',
          width: 200,
          margin: 1,
          errorCorrectionLevel: 'M'
        })
        this.qrImage = 'data:image/svg+xml;base64,'
          + this.toBase64(svg)
      } catch (error) {
        /* 手で入力する道が残っている。**黙って鍵だけ見せる。** */
        this.qrImage = ''
      }
    },

    /** SVGを data: へ入れる形にする。 */
    toBase64 (text) {
      if (typeof window !== 'undefined' && window.btoa) {
        return window.btoa(unescape(encodeURIComponent(text)))
      }
      /* 検査の環境では window.btoa が無いことがある。 */
      return Buffer.from(text, 'utf8').toString('base64')
    },


    async beginEnroll () {
      if (this.busy) return
      this.busy = true
      this.message = ''
      this.errorMessage = ''
      try {
        const options = await startEnrollment()
        this.secret = (options && options.secretBase32) || ''
        await this.drawQr(options)
        if (!this.secret) {
          /*
           * 鍵が返らないのに登録の画面へ進めない。進めると、入力欄だけ
           * 出て何も登録できない状態になる。
           */
          this.errorMessage = '2要素認証の設定を始められませんでした。'
          return
        }
        this.code = ''
        this.enrolling = true
      } catch (error) {
        this.errorMessage = this.explain(error,
          '2要素認証の設定を始められませんでした。')
      } finally {
        this.busy = false
      }
    },

    cancelEnroll () {
      /*
       * サーバ側の途中の行は残るが、確定していないので使えない。
       * もう一度始めれば新しい鍵が出る。**古い鍵は使えなくなる。**
       */
      this.enrolling = false
      this.secret = ''
      this.qrImage = ''
      this.code = ''
    },

    async confirm () {
      if (this.busy || !this.code) return
      this.busy = true
      this.errorMessage = ''
      try {
        const codes = await confirmEnrollment(this.code.trim())
        this.enrolling = false
        this.secret = ''
        this.qrImage = ''
        this.code = ''
        this.backupCodes = codes
        if (!codes.length) {
          /*
           * 予備が0本のまま「登録できました」と出さない。出すと、
           * スマホを失くしたときに入れないことに気づけない。
           */
          this.errorMessage = '登録はできましたが、予備のコードを受け取れません'
            + 'でした。「予備のコードを配り直す」から取り直してください。'
        }
        await this.reload()
      } catch (error) {
        this.errorMessage = this.explain(error,
          'この6桁では登録できませんでした。アプリに出ている数字を'
          + 'もう一度お確かめください。')
      } finally {
        this.busy = false
      }
    },

    acknowledgeCodes () {
      this.backupCodes = []
      this.message = '2要素認証を設定しました。'
        + ' 次のログインから6桁を聞かれます。'
    },

    /**
     * 付け替えを始める。**まず、いまの要素を出してもらう。**
     *
     * ここで鍵を先に見せる作りにすると、いまの要素を出せない相手にも
     * 新しい鍵が見えてしまう。**サーバが確かめてから返す。**
     */
    askCurrentFactor () {
      if (this.busy) return
      this.message = ''
      this.errorMessage = ''
      this.code = ''
      this.secret = ''
      this.qrImage = ''
      this.rotationId = ''
      this.rotating = 'verify'
    },

    /** いまの要素を出して、新しい鍵を受け取る。 */
    async beginRotation () {
      if (this.busy || !this.code) return
      this.busy = true
      this.errorMessage = ''
      try {
        const options = await startRotation(this.code.trim())
        this.secret = (options && options.secretBase32) || ''
        await this.drawQr(options)
        this.rotationId = (options && options.rotationId) || ''
        if (!this.secret || !this.rotationId) {
          /*
           * 鍵が返らないのに次へ進めない。進めると、入力欄だけ出て
           * 何も登録できない状態になる。
           */
          this.errorMessage = '認証アプリを付け替えられませんでした。'
          return
        }
        this.code = ''
        this.rotating = 'scan'
      } catch (error) {
        this.errorMessage = this.explain(error,
          'この6桁では確かめられませんでした。'
          + 'いま使っている認証アプリの数字か、予備のコードをお使いください。')
      } finally {
        this.busy = false
      }
    },

    /** 新しい端末の6桁で入れ替える。**ここで初めて入れ替わる。** */
    async finishRotation () {
      if (this.busy || !this.code) return
      this.busy = true
      this.errorMessage = ''
      try {
        const codes = await confirmRotation(
          this.rotationId, this.code.trim())
        this.rotating = ''
        this.secret = ''
        this.qrImage = ''
        this.rotationId = ''
        this.code = ''
        this.backupCodes = codes
        if (!codes.length) {
          /*
           * 予備が0本のまま「付け替えました」と出さない。出すと、
           * 新しいスマホを失くしたときに入れないことに気づけない。
           */
          this.errorMessage = '付け替えはできましたが、予備のコードを受け取れません'
            + 'でした。「予備のコードを配り直す」から取り直してください。'
        }
        await this.reload()
      } catch (error) {
        this.errorMessage = this.explain(error,
          'この6桁では付け替えられませんでした。新しい端末に出ている数字を'
          + 'もう一度お確かめください。')
        /*
         * **ここで rotating を戻さない。**戻すと、1段目からやり直しに
         * なる。新しい端末はもう登録できているので、打ち直すだけで済む。
         */
      } finally {
        this.busy = false
      }
    },

    /**
     * 付け替えをやめる。
     *
     * <b>古い認証アプリはそのまま使える。</b>サーバは2段目まで何も
     * 書き替えていない。
     */
    async cancelRotation () {
      const rotationId = this.rotationId
      this.rotating = ''
      this.secret = ''
      this.qrImage = ''
      this.rotationId = ''
      this.code = ''

      /*
       * **サーバの預かりぶんも捨てる。**画面を閉じるだけにすると、
       * 「やめた」のにサーバではまだ仕上げられる状態が残る。
       *
       * 捨てられなくても画面はもう閉じている。**ここで謝らない。**
       */
      try {
        await cancelRotation(rotationId)
      } catch (ignored) {
        /* 預かりぶんは古くなれば押し出される。ここでは何も言わない。 */
      }
    },

    async reissue () {
      if (this.busy) return
      this.busy = true
      this.message = ''
      this.errorMessage = ''
      try {
        /*
         * **いま見ている組を渡す。**別の画面が先に配り直していたら、
         * サーバが何も消さずに断る。渡さないと、あとから押したほうが
         * 先に画面へ出した10本を消してしまう。
         */
        this.backupCodes = await reissueBackupCodes(
          this.status.backupCodeBatch)
        await this.reload()
      } catch (error) {
        this.errorMessage = this.explain(error,
          '予備のコードを配り直せませんでした。')
        /*
         * 断られたら、こちらの画面はもう古い可能性がある。
         * **読み直してから、いま何本あるかを見せる。**
         */
        await this.reload()
      } finally {
        this.busy = false
      }
    },

    async disable () {
      if (this.busy) return
      this.busy = true
      this.message = ''
      this.errorMessage = ''
      try {
        await disableMfa()
        this.message = '2要素認証をやめました。'
        await this.reload()
      } catch (error) {
        this.errorMessage = this.explain(error,
          '2要素認証をやめられませんでした。')
      } finally {
        this.busy = false
      }
    },

    /**
     * 断られた理由の見せ方。
     *
     * <p><b>「ログインし直せば通る」と「何度やっても通らない」を分ける。</b>
     * 会社の方針で断られているときにログインし直させると、同じところで
     * また断られて、利用者は理由が分からないまま繰り返す。</p>
     */
    explain (error, fallback) {
      if (isReauthenticationRequired(error)) {
        return 'もう一度ログインの確認が必要です。'
          + ' 一度ログアウトして入り直してから、もう一度お試しください。'
      }
      if (isForbiddenByPolicy(error)) {
        /*
         * **「ログインし直しても変わらない」まで言う。**理由だけ出すと、
         * 利用者はもう一度ログインして同じところで断られる。断られた側は
         * 何が足りないのか分からないまま繰り返すことになる。
         */
        return (serverMessage(error) || 'この操作は許可されていません。')
          + ' ログインし直しても変わりません。'
      }
      if (isNetworkFailure(error)) {
        return '通信できませんでした。接続を確認してからお試しください。'
      }
      return serverMessage(error) || fallback
    }
  }
}
</script>
